// Code generated by @hex-insights/frontend-generator; While this comment is present this file may be overwritten. If manual edits are made, remove this comment.
import { getObjectKeys, NullPartial, omitKeys, pickKeys, subsetTypeHasKey, ValueOf } from "@hex-insights/core";
import {
	EnrollmentApplicationGuardianCreateInput,
	EnrollmentApplicationGuardianDetailQuery,
	EnrollmentApplicationGuardianInEnrollmentApplicationCreateInput,
	EnrollmentApplicationGuardianUpdateInput,
} from "../../../Utilities";
import * as EnrollmentApplicationGuardianFormValues from "./form-values";

/**
 * Converts the given `formValues` for the create form to a GraphQL create input for the EnrollmentApplicationGuardian model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInput<
	K extends keyof EnrollmentApplicationGuardianFormValues.Create = keyof EnrollmentApplicationGuardianFormValues.Create,
>(formValues: EnrollmentApplicationGuardianFormValues.CreateSubset<K>): EnrollmentApplicationGuardianCreateInput {
	const input: NullPartial<EnrollmentApplicationGuardianCreateInput> = { ...formValues };
	return input as EnrollmentApplicationGuardianCreateInput;
}

/**
 * Converts the given `formValues` for the create form nested in the EnrollmentApplication to a GraphQL nested create input for the EnrollmentApplicationGuardian model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInEnrollmentApplicationInput<
	K extends keyof EnrollmentApplicationGuardianFormValues.CreateInEnrollmentApplication = keyof EnrollmentApplicationGuardianFormValues.CreateInEnrollmentApplication,
>(
	formValues: EnrollmentApplicationGuardianFormValues.CreateInEnrollmentApplicationSubset<K>,
): EnrollmentApplicationGuardianInEnrollmentApplicationCreateInput {
	const input: NullPartial<EnrollmentApplicationGuardianInEnrollmentApplicationCreateInput> = { ...formValues };
	return input as EnrollmentApplicationGuardianInEnrollmentApplicationCreateInput;
}

/**
 * Converts the given `formValues` for the detail form to a GraphQL update input for the EnrollmentApplicationGuardian model.
 *
 * @param formValues The form values to convert. Generally only the changed form values.
 * @param initialFormValues The initial values of the form.
 */
export function toGQLUpdateInput<
	K extends keyof EnrollmentApplicationGuardianFormValues.Detail = keyof EnrollmentApplicationGuardianFormValues.Detail,
>(
	formValues: Partial<EnrollmentApplicationGuardianFormValues.DetailSubset<K>>,
	_initialFormValues?: EnrollmentApplicationGuardianFormValues.DetailSubset<K>,
): EnrollmentApplicationGuardianUpdateInput {
	const inputFormValues = omitKeys(formValues, ["name"]);
	const input: EnrollmentApplicationGuardianUpdateInput = { ...inputFormValues };

	if (subsetTypeHasKey(formValues, "dateOfBirth") && formValues.dateOfBirth === null) {
		input.clearDateOfBirth = true;
	}

	if (subsetTypeHasKey(formValues, "relationshipStatus") && formValues.relationshipStatus === null) {
		input.clearRelationshipStatus = true;
	}

	if (subsetTypeHasKey(formValues, "addressCountryID") && formValues.addressCountryID === null) {
		input.addressCountryID = null;
		input.clearAddressCountry = true;
	}

	if (subsetTypeHasKey(formValues, "guardianID") && formValues.guardianID === null) {
		input.guardianID = null;
		input.clearGuardian = true;
	}

	if (subsetTypeHasKey(formValues, "targetAddressGuardianID") && formValues.targetAddressGuardianID === null) {
		input.targetAddressGuardianID = null;
		input.clearTargetAddressGuardian = true;
	}

	return input;
}

export type EnrollmentApplicationGuardianFormKeyMap = {
	name: "name";
	givenName: "givenName";
	middleName: "middleName";
	familyName: "familyName";
	nameOrder: "nameOrder";
	dateOfBirth: "dateOfBirth";
	gender: "gender";
	relationshipType: "relationshipType";
	relationshipStatus: "relationshipStatus";
	emailAddress: "emailAddress";
	phoneNumber: "phoneNumber";
	addressLine1: "addressLine1";
	addressLine2: "addressLine2";
	addressCity: "addressCity";
	addressPostalCode: "addressPostalCode";
	addressStateOrProvince: "addressStateOrProvince";
	isPrimary: "isPrimary";
	isFinancialContact: "isFinancialContact";
	isEmergencyContact: "isEmergencyContact";
	isAuthorizedForPickup: "isAuthorizedForPickup";
	livesWithStudent: "livesWithStudent";
	isPrimaryOnApplication: "isPrimaryOnApplication";
	addressCountryID: "addressCountry";
	enrollmentApplicationID: "enrollmentApplication";
	guardianID: "guardian";
	targetAddressGuardianID: "targetAddressGuardian";
};

export type ToFormValuesArgs<
	K extends keyof EnrollmentApplicationGuardianFormValues.Detail = keyof EnrollmentApplicationGuardianFormValues.Detail,
> = {
	enrollmentApplicationGuardian: Pick<
		EnrollmentApplicationGuardianDetailQuery["enrollmentApplicationGuardian"],
		ValueOf<Pick<EnrollmentApplicationGuardianFormKeyMap, K>>
	>;
	fields?: { [Key in K]: true };
};

/**
 * Converts a EnrollmentApplicationGuardian instance to its corresponding detail form values.
 *
 * @param enrollmentApplicationGuardian The instance to convert.
 * @param fields The subset of fields to include.
 */
export function toFormValues<
	K extends keyof EnrollmentApplicationGuardianFormValues.Detail = keyof EnrollmentApplicationGuardianFormValues.Detail,
>({
	enrollmentApplicationGuardian,
	fields,
}: ToFormValuesArgs<K>): EnrollmentApplicationGuardianFormValues.DetailSubset<K> {
	const formValues = {
		name: subsetTypeHasKey(enrollmentApplicationGuardian, "name") ? enrollmentApplicationGuardian.name : "",
		givenName: subsetTypeHasKey(enrollmentApplicationGuardian, "givenName")
			? enrollmentApplicationGuardian.givenName
			: "",
		middleName: subsetTypeHasKey(enrollmentApplicationGuardian, "middleName")
			? enrollmentApplicationGuardian.middleName
			: "",
		familyName: subsetTypeHasKey(enrollmentApplicationGuardian, "familyName")
			? enrollmentApplicationGuardian.familyName
			: "",
		nameOrder: subsetTypeHasKey(enrollmentApplicationGuardian, "nameOrder")
			? enrollmentApplicationGuardian.nameOrder
			: null,
		dateOfBirth: subsetTypeHasKey(enrollmentApplicationGuardian, "dateOfBirth")
			? enrollmentApplicationGuardian.dateOfBirth
			: null,
		gender: subsetTypeHasKey(enrollmentApplicationGuardian, "gender") ? enrollmentApplicationGuardian.gender : "",
		relationshipType: subsetTypeHasKey(enrollmentApplicationGuardian, "relationshipType")
			? enrollmentApplicationGuardian.relationshipType
			: "",
		relationshipStatus: subsetTypeHasKey(enrollmentApplicationGuardian, "relationshipStatus")
			? enrollmentApplicationGuardian.relationshipStatus
			: null,
		emailAddress: subsetTypeHasKey(enrollmentApplicationGuardian, "emailAddress")
			? enrollmentApplicationGuardian.emailAddress
			: "",
		phoneNumber: subsetTypeHasKey(enrollmentApplicationGuardian, "phoneNumber")
			? enrollmentApplicationGuardian.phoneNumber
			: "",
		addressLine1: subsetTypeHasKey(enrollmentApplicationGuardian, "addressLine1")
			? enrollmentApplicationGuardian.addressLine1
			: "",
		addressLine2: subsetTypeHasKey(enrollmentApplicationGuardian, "addressLine2")
			? enrollmentApplicationGuardian.addressLine2
			: "",
		addressCity: subsetTypeHasKey(enrollmentApplicationGuardian, "addressCity")
			? enrollmentApplicationGuardian.addressCity
			: "",
		addressPostalCode: subsetTypeHasKey(enrollmentApplicationGuardian, "addressPostalCode")
			? enrollmentApplicationGuardian.addressPostalCode
			: "",
		addressStateOrProvince: subsetTypeHasKey(enrollmentApplicationGuardian, "addressStateOrProvince")
			? enrollmentApplicationGuardian.addressStateOrProvince
			: "",
		isPrimary: subsetTypeHasKey(enrollmentApplicationGuardian, "isPrimary")
			? enrollmentApplicationGuardian.isPrimary
			: false,
		isFinancialContact: subsetTypeHasKey(enrollmentApplicationGuardian, "isFinancialContact")
			? enrollmentApplicationGuardian.isFinancialContact
			: false,
		isEmergencyContact: subsetTypeHasKey(enrollmentApplicationGuardian, "isEmergencyContact")
			? enrollmentApplicationGuardian.isEmergencyContact
			: false,
		isAuthorizedForPickup: subsetTypeHasKey(enrollmentApplicationGuardian, "isAuthorizedForPickup")
			? enrollmentApplicationGuardian.isAuthorizedForPickup
			: false,
		livesWithStudent: subsetTypeHasKey(enrollmentApplicationGuardian, "livesWithStudent")
			? enrollmentApplicationGuardian.livesWithStudent
			: false,
		isPrimaryOnApplication: subsetTypeHasKey(enrollmentApplicationGuardian, "isPrimaryOnApplication")
			? enrollmentApplicationGuardian.isPrimaryOnApplication
			: false,
		addressCountryID: subsetTypeHasKey(enrollmentApplicationGuardian, "addressCountry")
			? enrollmentApplicationGuardian.addressCountry?.id ?? null
			: null,
		enrollmentApplicationID: subsetTypeHasKey(enrollmentApplicationGuardian, "enrollmentApplication")
			? enrollmentApplicationGuardian.enrollmentApplication?.id ?? null
			: null,
		guardianID: subsetTypeHasKey(enrollmentApplicationGuardian, "guardian")
			? enrollmentApplicationGuardian.guardian?.id ?? null
			: null,
		targetAddressGuardianID: subsetTypeHasKey(enrollmentApplicationGuardian, "targetAddressGuardian")
			? enrollmentApplicationGuardian.targetAddressGuardian?.id ?? null
			: null,
	};
	return fields ? pickKeys(formValues, getObjectKeys(fields)) : formValues;
}
